const roots = [...document.querySelectorAll('[data-corim-hcaptcha]')];

if (roots.length > 0) {
    const sdkId = 'corim-hcaptcha-sdk';
    const sdkTimeoutMs = 12000;
    const intentEvents = ['keydown', 'pointerdown', 'pointermove', 'touchstart', 'wheel'];
    let sdkPromise = null;

    const setStatus = (root, message) => {
        const status = root.querySelector('[data-corim-hcaptcha-status]');
        if (status) {
            status.textContent = message;
        }
    };

    const hideError = (root) => {
        root.querySelector('[data-corim-hcaptcha-error]')?.classList.add('d-none');
        const message = root.querySelector('[data-corim-hcaptcha-error-message]');
        if (message) {
            message.textContent = '';
        }
    };

    const showError = (root, message) => {
        const wrapper = root.querySelector('[data-corim-hcaptcha-error]');
        const messageElement = root.querySelector('[data-corim-hcaptcha-error-message]');
        if (messageElement) {
            messageElement.textContent = message;
        }
        wrapper?.classList.remove('d-none');
        setStatus(root, message);
    };

    const renderWidget = (root) => {
        if (root.dataset.widgetId !== undefined || !window.hcaptcha?.render) {
            return;
        }

        const target = root.querySelector('[data-corim-hcaptcha-target]');
        if (!target) {
            return;
        }

        const widgetId = window.hcaptcha.render(target, {
            sitekey: root.dataset.sitekey,
            theme: root.dataset.theme || 'dark',
            callback: () => {
                hideError(root);
                setStatus(root, root.dataset.messageReady);
            },
            'expired-callback': () => {
                showError(root, root.dataset.messageRequired);
            },
            'chalexpired-callback': () => {
                showError(root, root.dataset.messageRequired);
            },
            'error-callback': () => {
                showError(root, root.dataset.messageUnavailable);
            },
        });
        root.dataset.widgetId = String(widgetId);
        setStatus(root, root.dataset.messageReady);
    };

    const renderWidgets = () => roots.forEach(renderWidget);

    const resetSdkState = () => {
        document.getElementById(sdkId)?.remove();
        delete window.corimHcaptchaOnload;
        sdkPromise = null;
    };

    const load = () => {
        if (window.hcaptcha?.render) {
            try {
                renderWidgets();
                return Promise.resolve(window.hcaptcha);
            } catch (error) {
                roots.forEach((root) => showError(root, root.dataset.messageUnavailable));
                return Promise.reject(error);
            }
        }
        if (sdkPromise) {
            return sdkPromise;
        }

        roots.forEach((root) => setStatus(root, root.dataset.messageLoading));
        const language = roots[0].dataset.language === 'en' ? 'en' : 'fr';
        sdkPromise = new Promise((resolve, reject) => {
            const timeout = window.setTimeout(() => {
                resetSdkState();
                reject(new Error('hCaptcha SDK timeout'));
            }, sdkTimeoutMs);
            const fail = () => {
                window.clearTimeout(timeout);
                resetSdkState();
                reject(new Error('hCaptcha SDK unavailable'));
            };

            window.corimHcaptchaOnload = () => {
                window.clearTimeout(timeout);
                delete window.corimHcaptchaOnload;
                if (!window.hcaptcha?.render) {
                    fail();
                    return;
                }
                try {
                    renderWidgets();
                    resolve(window.hcaptcha);
                } catch {
                    fail();
                }
            };

            const script = document.createElement('script');
            script.id = sdkId;
            script.async = true;
            script.defer = true;
            script.onerror = fail;
            script.src = `https://js.hcaptcha.com/1/api.js?onload=corimHcaptchaOnload&render=explicit&recaptchacompat=off&hl=${language}`;
            document.head.appendChild(script);
        });

        sdkPromise.catch(() => {
            roots.forEach((root) => showError(root, root.dataset.messageUnavailable));
        });
        return sdkPromise;
    };

    const focusWidget = (root) => {
        window.setTimeout(() => {
            root.querySelector('iframe[title]')?.focus();
        }, 0);
    };

    const hasValidResponse = (root) => {
        if (root.dataset.widgetId === undefined || !window.hcaptcha?.getResponse) {
            return false;
        }
        return window.hcaptcha.getResponse(root.dataset.widgetId).trim() !== '';
    };

    roots.forEach((root) => {
        const form = root.closest('form');
        form?.addEventListener('submit', (event) => {
            if (hasValidResponse(root)) {
                hideError(root);
                return;
            }

            event.preventDefault();
            event.stopImmediatePropagation();
            showError(root, root.dataset.messageRequired);
            load().then(() => focusWidget(root)).catch(() => {});
        }, true);

        form?.addEventListener('reset', () => {
            hideError(root);
            if (root.dataset.widgetId !== undefined && window.hcaptcha?.reset) {
                window.hcaptcha.reset(root.dataset.widgetId);
            }
        });

        root.querySelector('[data-corim-hcaptcha-retry]')?.addEventListener('click', () => {
            hideError(root);
            load().then(() => {
                if (root.dataset.widgetId !== undefined && window.hcaptcha?.reset) {
                    window.hcaptcha.reset(root.dataset.widgetId);
                }
                focusWidget(root);
            }).catch(() => {});
        });
    });

    const onUserIntent = (event) => {
        if (!event.isTrusted) {
            return;
        }
        intentEvents.forEach((eventName) => document.removeEventListener(eventName, onUserIntent, true));
        load().catch(() => {});
    };
    intentEvents.forEach((eventName) => document.addEventListener(eventName, onUserIntent, {
        capture: true,
        passive: true,
    }));

    window.corimHcaptcha = Object.freeze({ load });
}
